import { Modal, Button, Row, Col, message, Tag, Segmented } from "antd";
import React, { useEffect, useState } from "react";
import Draggable from "react-draggable";
import DropdownSelect from "../../../components/generic/DropdownSelect";
import { getAllEnvironment } from "../../../service/admin.service";
import { getCurrentDateTimeInFormat } from "../../../helper/date_helper";
import { createSuiteJob, pollJobStatus } from "../../../service/run.service";
import MasterSuiteViewTable from "../MasterSuiteViewTable";
import MasterSuiteFlow from "../../../components/mastersuite/MasterSuiteFlow";
import { toFlowNodes } from "../../../helper/masterSuiteFlow";
import {
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  PartitionOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { useProject } from "../../../context/ProjectContext";
import ScheduleForm, {
  INITIAL_SCHEDULE,
  validateSchedule,
  buildSchedulePayload,
} from "./ScheduleForm";
import { createSchedule } from "../../../service/schedule.service";

const MODE_OPTIONS = [
  {
    value: "run",
    label: "Run now",
    desc: "Start the master suite immediately",
    icon: <PlayCircleOutlined />,
  },
  {
    value: "schedule",
    label: "Schedule for later",
    desc: "Pick a frequency, start & end time",
    icon: <ClockCircleOutlined />,
  },
];

// On-brand selectable cards — active = accent blue on a soft tint, navy text.
function ModeCard({ option, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 12,
        textAlign: "left",
        fontFamily: "inherit",
        cursor: "pointer",
        padding: "12px 16px",
        borderRadius: 10,
        border: `1.5px solid ${active ? "#185FA5" : "#E4E7EB"}`,
        background: active ? "rgba(24, 95, 165, 0.06)" : "#fff",
        boxShadow: active ? "0 1px 4px rgba(24, 95, 165, 0.12)" : "none",
        transition: "border-color .15s ease, background .15s ease",
      }}
    >
      <span
        style={{
          fontSize: 22,
          lineHeight: 1,
          color: active ? "#185FA5" : "#6C8A9B",
        }}
      >
        {option.icon}
      </span>
      <span>
        <span
          style={{
            display: "block",
            fontSize: 14,
            fontWeight: 600,
            color: active ? "#0B1F3A" : "#2D3748",
          }}
        >
          {option.label}
        </span>
        <span style={{ display: "block", fontSize: 12, color: "#8A8A8A" }}>
          {option.desc}
        </span>
      </span>
    </button>
  );
}

function RunModal(props) {
  const { email } = useUser();
  const { activeProject } = useProject();
  const navigate = useNavigate();
  // The environment to pre-select: the master suite's own default, else the active
  // project's default_environment. Either is still overridable via the picker.
  const effectiveDefaultEnv =
    props.defaultEnvironment || activeProject?.defaultEnvironment || "";
  const [disabled, setDisabled] = useState(true);
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [mode, setMode] = useState("run"); // "run" | "schedule"
  // Workflow reads better for shape — what runs after what, and which steps branch.
  // Table stays available because it is the only place to open a step's parameters or
  // to see which rows an environment change affects.
  const [stepView, setStepView] = useState("Workflow");
  const [schedule, setSchedule] = useState(INITIAL_SCHEDULE);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });
  const draggleRef = React.createRef();
  const onStart = (event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) return;
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };
  useEffect(() => {
    if (props.open) {
      getAllEnvironment().then((response) => {
        setEnvironments(response.data);
      });
      setSelectedEnvironment(effectiveDefaultEnv);
      setMode("run");
      setStepView("Workflow");
      setSchedule(INITIAL_SCHEDULE);
    }
  }, [effectiveDefaultEnv, props.open]);
  const onEnvironmentChange = (value) => {
    setSelectedEnvironment(value);
    value === effectiveDefaultEnv
      ? setIsDefault(true)
      : setIsDefault(false);
  };
  // Steps that legitimately run without parameters are exempt from the
  // missing-parameters check: UI/Citrix automation suites carry no variables,
  // and a "Master Suite" reference step has none either.
  const stepNeedsParameters = (step) => {
    if (step.type === "Master Suite") return false;
    const category = (step.categoryValue || step.category || "").toLowerCase();
    if (category === "ui automation" || category === "ui") return false;
    if ((step.subCategoryValue || "").toLowerCase() === "ui test") return false;
    return true;
  };

  const onRun = () => {
    const hasBlankVariables = props.stepData.some(
      (step) => stepNeedsParameters(step) && !step.variables,
    );
    if (hasBlankVariables) {
      message.error(
        "One or more steps is missing parameters. Please review and rectify before running.",
      );
      return;
    }
    const runStart = getCurrentDateTimeInFormat();
    props.handleCancel();
    message.info(
      `Master suite "${props.masterSuiteName}" is running. You will be notified when complete.`,
    );
    createSuiteJob(
      selectedEnvironment,
      runStart,
      email,
      "",
      "In Progress",
      props.masterSuiteName,
      props.steps,
    )
      .then((res) => {
        // Refresh the library so the row immediately shows "In Progress"
        // (this also kicks off the auto-refresh poll on the library page)
        props.onRunStarted?.();
        const jobId = res.data?.jobId;
        if (!jobId) return;
        // Take the user straight to the live run view — no manual hop to Jobs
        navigate("/jobs/details", { state: { jobId } });
        pollJobStatus(jobId)
          .then(() => {
            message.success(
              `Master suite "${props.masterSuiteName}" completed successfully.`,
            );
            props.onRunStarted?.();
          })
          .catch(() => {
            message.error(
              `Master suite "${props.masterSuiteName}" completed with errors — check the Jobs page for detail.`,
            );
            props.onRunStarted?.();
          });
      })
      .catch(() => {
        message.error("Failed to start master suite — please try again.");
      });
  };

  const onSchedule = () => {
    if (!selectedEnvironment) {
      message.error("Please select an environment.");
      return;
    }
    const err = validateSchedule(schedule);
    if (err) {
      message.error(err);
      return;
    }
    const payload = buildSchedulePayload(
      schedule,
      selectedEnvironment,
      props.masterSuiteName,
    );
    createSchedule(payload)
      .then(() => {
        message.success(`Schedule created for "${props.masterSuiteName}".`);
        props.handleCancel();
      })
      .catch((e) => {
        const data = e?.response?.data;
        const msg = (data && (data.message || data.error)) || data;
        message.error(
          typeof msg === "string" ? msg : "Failed to create schedule.",
        );
      });
  };

  return (
    <div>
      <Modal
        open={props.open}
        onCancel={props.handleCancel}
        width="90%"
        footer={[
          mode === "run" ? (
            <Button key="run" onClick={onRun}>
              Run
            </Button>
          ) : (
            <Button key="schedule" onClick={onSchedule}>
              Schedule
            </Button>
          ),
          <Button key="back" type="primary" onClick={props.handleCancel}>
            Cancel
          </Button>,
        ]}
        modalRender={(modal) => (
          <Draggable
            disabled={disabled}
            bounds={bounds}
            onStart={(event, uiData) => onStart(event, uiData)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )}
        title={
          <div
            style={{ width: "100%", cursor: "move" }}
            onMouseOver={() => setDisabled(false)}
            onMouseOut={() => setDisabled(true)}
          >
            Run Master Suite {props.masterSuiteName}
          </div>
        }
      >
        <div style={{ display: "flex", gap: 12, paddingTop: 4 }}>
          {MODE_OPTIONS.map((opt) => (
            <ModeCard
              key={opt.value}
              option={opt}
              active={mode === opt.value}
              onSelect={setMode}
            />
          ))}
        </div>

        <Row style={{ paddingTop: "20px" }}>
          <Col span={6}>
            <p className="spec-modal-label">Environment</p>
          </Col>
          <Col span={18}>
            <DropdownSelect
              value={selectedEnvironment}
              options={environments || []}
              labelKey="displayName"
              valueKey="displayName"
              onSelect={onEnvironmentChange}
            />
          </Col>
        </Row>

        {mode === "schedule" && (
          <ScheduleForm value={schedule} onChange={setSchedule} />
        )}

        {!isDefault && (
          <Row style={{ paddingTop: "20px" }}>
            <Tag color="red" icon={<ExclamationCircleOutlined />}>
              {stepView === "Table"
                ? "Based on your environment selection, the variables of the rows highlighted in red will be affected"
                : "Based on your environment selection, some step variables will be affected — switch to Table view to see which"}
            </Tag>
          </Row>
        )}

        <Row style={{ paddingTop: "20px" }}>
          <Col span={24}>
            <Segmented
              size="small"
              value={stepView}
              onChange={setStepView}
              style={{ marginBottom: 12 }}
              options={[
                {
                  label: "Workflow",
                  value: "Workflow",
                  icon: <PartitionOutlined />,
                },
                { label: "Table", value: "Table", icon: <TableOutlined /> },
              ]}
            />
            {stepView === "Workflow" ? (
              // Read-only: no drag, no insert "+", no add tile — this is a run dialog,
              // not the builder. No title either; the toggle above already labels it.
              <MasterSuiteFlow
                nodes={toFlowNodes(props.stepData, "builder")}
                mode="view"
              />
            ) : (
              <MasterSuiteViewTable
                dataSource={props.stepData}
                environment={selectedEnvironment}
              />
            )}
          </Col>
        </Row>
      </Modal>
    </div>
  );
}

export default RunModal;
